import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Check, X, PalmtreeIcon } from "lucide-react";

export default function PtoQueuePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pto/approvals/pending");
      setItems(data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try {
      await api.post(`/pto/requests/${id}/approve`);
      toast.success("Approved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submitReject = async () => {
    setBusy(true);
    try {
      await api.post(`/pto/requests/${rejecting.id}/reject`, { reason });
      toast.success("Rejected");
      setRejecting(null); setReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="pto-queue-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Approvals</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">PTO — pending</h1>
        <p className="mt-2 text-muted-foreground">Time-off requests routed to you.</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary/10 text-secondary"><PalmtreeIcon className="h-8 w-8" /></div>
            <div className="mt-4 font-medium">No pending PTO requests</div>
            <p className="mt-1 text-sm text-muted-foreground">You'll see requests here as workers submit them.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-left">
                <th className="p-3">Worker</th>
                <th className="p-3">Type</th>
                <th className="p-3">Period</th>
                <th className="p-3">Hours/day</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`pto-pending-${r.id}`}>
                  <td className="p-3">
                    <div className="font-medium">{r.worker_name}</div>
                    <div className="text-xs text-muted-foreground">{r.worker_email}</div>
                  </td>
                  <td className="p-3">
                    <div className="inline-flex items-center gap-2">
                      <span className="inline-block h-3 w-3 rounded" style={{ backgroundColor: r.pto_type_color || "#8b95a7" }} />
                      {r.pto_type_label}
                    </div>
                    <div className="text-xs text-muted-foreground">{r.pto_type_code}{!r.pto_type_paid && " · Unpaid"}</div>
                  </td>
                  <td className="p-3">{r.start_date}{r.start_date !== r.end_date && ` → ${r.end_date}`}
                    {r.reason && <div className="mt-1 text-xs italic text-muted-foreground">{r.reason}</div>}
                  </td>
                  <td className="p-3 tabular-nums">{r.hours_per_day}h</td>
                  <td className="p-3 space-x-2">
                    <Button size="sm" onClick={() => approve(r.id)} data-testid={`pto-approve-${r.id}`}><Check className="h-3 w-3" /> Approve</Button>
                    <Button size="sm" variant="destructive" onClick={() => setRejecting(r)} data-testid={`pto-reject-${r.id}`}><X className="h-3 w-3" /> Reject</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!rejecting} onOpenChange={(v) => !v && setRejecting(null)}>
        <DialogContent data-testid="pto-reject-dialog">
          <DialogHeader><DialogTitle>Reject PTO</DialogTitle></DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" rows={4} data-testid="pto-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)}>Cancel</Button>
            <Button variant="destructive" onClick={submitReject} disabled={busy || !reason.trim()} data-testid="pto-reject-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
